import {
  Box, Image, Text, VStack, HStack, AspectRatio,
  Skeleton, SkeletonText, Icon, Badge, IconButton,
} from "@chakra-ui/react";
import { FaMapMarkerAlt, FaClock, FaTruck, FaHeart, FaRegHeart, FaEye } from "react-icons/fa";
import React, { useState } from "react";
import { useLang } from "../../../contexts/LangContext";
import { useFavorites } from "../../../contexts/FavoritesContext";

const RED = "#a21b1b";

function getCloudinaryResized(url, width) {
  if (!url || !url.includes("res.cloudinary.com")) return url;
  return url.replace("/upload/", `/upload/w_${width},f_auto,q_65/`);
}

export default function ProductCard({ product, lcp = false }) {
  const [imgReady, setImgReady] = useState(lcp);
  const { t } = useLang();
  const { isFavorited, toggle } = useFavorites();
  const authToken = localStorage.getItem("authToken");

  const productId  = product._id || product.id;
  const favorited  = isFavorited(productId);

  const rawAddr  = product.address;
  const address  = Array.isArray(rawAddr) ? (rawAddr[0] || {}) : (rawAddr || {});
  const category = product.catagory || product.category || "";

  const createdAt = new Date(product.createdAt);
  const now       = new Date();
  const daysAgo   = Math.floor((now - createdAt) / (1000 * 60 * 60 * 24));

  const formatPrice = (price) => {
    if (!price) return t("price_on_request");
    const num = parseFloat(price);
    if (isNaN(num)) return price;
    return new Intl.NumberFormat("fr-DZ", { maximumFractionDigits: 0 }).format(num) + " " + t("currency");
  };

  const timeAgo = () => {
    if (daysAgo === 0) return t("today");
    if (daysAgo === 1) return t("yesterday");
    if (daysAgo < 7) return `${daysAgo}j`;
    if (daysAgo < 30) return `${Math.floor(daysAgo / 7)}sem`;
    return `${Math.floor(daysAgo / 30)}mois`;
  };

  const handleFavorite = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (authToken) toggle(productId);
  };

  const views          = product.views ?? 0;
  const favoritesCount = product.favoritesCount ?? 0;

  return (
    <Box
      bg="white"
      borderRadius="2xl"
      overflow="hidden"
      border="1px solid"
      borderColor="gray.100"
      boxShadow="0 2px 8px rgba(0,0,0,0.06)"
      transition="box-shadow 0.2s, transform 0.2s"
      _hover={{ boxShadow: "0 8px 24px rgba(0,0,0,0.13)", transform: "translateY(-3px)" }}
      cursor="pointer"
      h="100%"
      display="flex"
      flexDirection="column"
    >
      {/* Image */}
      <Box position="relative" overflow="hidden">
        <AspectRatio ratio={3 / 4}>
          {/* Image always in DOM — skeleton overlay on top until ready */}
          <Box bg="gray.100" position="relative">
            <Image
              src={getCloudinaryResized(product.productpic1, 320)}
              alt={product.title}
              objectFit="cover"
              w="100%" h="100%"
              loading={lcp ? "eager" : "lazy"}
              fetchPriority={lcp ? "high" : "auto"}
              onLoad={() => setImgReady(true)}
              transition="transform 0.3s ease"
              _hover={{ transform: "scale(1.04)" }}
              fallbackSrc="https://placehold.co/400x300?text=?"
            />
            {!imgReady && (
              <Skeleton
                position="absolute"
                top={0} left={0} right={0} bottom={0}
                startColor="gray.100"
                endColor="gray.200"
                zIndex={1}
                pointerEvents="none"
              />
            )}
          </Box>
        </AspectRatio>

        {/* Category chip */}
        {category && (
          <Box position="absolute" top={2} left={2}
            bg="white" color="gray.700" fontSize="10px"
            px={2.5} py={0.5} borderRadius="full" fontWeight="600"
            boxShadow="0 1px 6px rgba(0,0,0,0.18)" noOfLines={1} maxW="60%"
          >
            {category}
          </Box>
        )}

        {/* Heart button — bottom-left, above all overlays */}
        {authToken && (
          <IconButton
            icon={<Icon as={favorited ? FaHeart : FaRegHeart} boxSize={4} />}
            position="absolute" bottom={2} left={2}
            size="md" minW="44px" minH="44px" borderRadius="full"
            bg={favorited ? "red.500" : "whiteAlpha.900"}
            color={favorited ? "white" : "gray.600"}
            boxShadow="0 2px 8px rgba(0,0,0,0.22)"
            _hover={{ bg: favorited ? "red.600" : "white", color: favorited ? "white" : RED, transform: "scale(1.1)" }}
            transition="all 0.15s"
            onClick={handleFavorite}
            aria-label={favorited ? "Retirer des favoris" : "Ajouter aux favoris"}
            zIndex={3}
          />
        )}

        {/* Status overlays */}
        {product.status === "RESERVED" && (
          <Box position="absolute" inset={0} bg="blackAlpha.300"
            display="flex" alignItems="flex-start" justifyContent="flex-end" p={2}>
            <Badge bg="orange.500" color="white" borderRadius="full" px={2.5} py={0.5}
              fontSize="10px" fontWeight="800" letterSpacing="wide">
              {t("status_reserved")}
            </Badge>
          </Box>
        )}
        {product.status === "SOLD" && (
          <Box position="absolute" inset={0} bg="blackAlpha.500"
            display="flex" alignItems="center" justifyContent="center">
            <Badge bg="gray.800" color="white" borderRadius="full" px={3} py={1}
              fontSize="11px" fontWeight="800" letterSpacing="widest" textTransform="uppercase">
              {t("status_sold")}
            </Badge>
          </Box>
        )}

        {/* Delivery badge */}
        {product.delivery && product.status === "AVAILABLE" && (
          <Badge position="absolute" bottom={2} right={2}
            bg="green.600" color="white" px={2.5} py={0.5} borderRadius="full"
            fontSize="10px" fontWeight="700" display="flex" alignItems="center" gap={1}>
            <Icon as={FaTruck} boxSize={2.5} mr={1} />
            {t("delivery_label")}
          </Badge>
        )}
      </Box>

      {/* Content */}
      <VStack p={{ base: 2.5, md: 3 }} align="stretch" spacing={1.5} flex="1" justify="space-between">
        <Box>
          <Text fontWeight="800" fontSize={{ base: "lg", md: "xl" }} color={RED} lineHeight="1.2" mb={1}>
            {formatPrice(product.price)}
          </Text>
          <Text fontSize={{ base: "sm", md: "md" }} color="gray.800"
            noOfLines={2} lineHeight="1.4" fontWeight="500">
            {product.title}
          </Text>
        </Box>

        <HStack justify="space-between" color="gray.700" fontSize="xs"
          pt={2} borderTop="1px solid" borderColor="gray.100">
          <HStack spacing={1} minW={0}>
            <Icon as={FaMapMarkerAlt} boxSize={3} color={RED} flexShrink={0} />
            <Text noOfLines={1} fontWeight="500">
              {address.city
                ? address.state && address.city !== address.state
                  ? `${address.city}, ${address.state}`
                  : address.city
                : address.state || t("country")}
            </Text>
          </HStack>
          <HStack spacing={2} flexShrink={0}>
            {favoritesCount > 0 && (
              <HStack spacing={0.5}>
                <Icon as={FaHeart} boxSize={3} color="red.500" />
                <Text color="red.500" fontWeight="600" whiteSpace="nowrap">
                  {favoritesCount > 999 ? `${(favoritesCount / 1000).toFixed(1)}k` : favoritesCount}
                </Text>
              </HStack>
            )}
            {views > 0 && (
              <HStack spacing={0.5}>
                <Icon as={FaEye} boxSize={3} color="gray.600" />
                <Text color="gray.600" whiteSpace="nowrap">{views > 999 ? `${(views / 1000).toFixed(1)}k` : views}</Text>
              </HStack>
            )}
            <HStack spacing={1}>
              <Icon as={FaClock} boxSize={3} color="gray.600" />
              <Text whiteSpace="nowrap" color="gray.600">{timeAgo()}</Text>
            </HStack>
          </HStack>
        </HStack>
      </VStack>
    </Box>
  );
}

export function ProductCardSkeleton() {
  return (
    <Box bg="white" borderRadius="2xl" overflow="hidden"
      border="1px solid" borderColor="gray.100"
      boxShadow="0 2px 8px rgba(0,0,0,0.06)">
      <AspectRatio ratio={3 / 4}>
        <Skeleton w="100%" h="100%" startColor="gray.100" endColor="gray.200" />
      </AspectRatio>
      <Box p={{ base: 2.5, md: 3 }}>
        <Skeleton h={5} w="55%" mb={2} borderRadius="md" />
        <SkeletonText noOfLines={2} spacing={2} skeletonHeight={3} />
        <HStack mt={3} spacing={2}>
          <Skeleton h={3} w="42%" borderRadius="full" />
          <Skeleton h={3} w="20%" borderRadius="full" />
        </HStack>
      </Box>
    </Box>
  );
}
